import React from 'react';
import { Container, Paper, Typography, Button, Box, Alert } from '@mui/material';
import { ErrorOutline, Refresh } from '@mui/icons-material';

/**
 * Catches JavaScript errors anywhere in the wrapped tree and shows a readable
 * message instead of a blank white page. Without this, ANY uncaught render
 * error (a bad field on one record, an unexpected null, etc.) unmounts the
 * whole app with zero on-screen indication of what happened.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary] Caught render error:', error, info);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return (
        <Container maxWidth="sm" sx={{ mt: 6 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <ErrorOutline color="error" sx={{ fontSize: 56, mb: 1 }} />
            <Typography variant="h5" gutterBottom>Something went wrong</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This page hit an unexpected error. The rest of the site is unaffected —
              try going back or reloading. If this keeps happening on the same record,
              please share the error message below so it can be fixed.
            </Typography>
            <Alert severity="error" sx={{ textAlign: 'left', mb: 2, wordBreak: 'break-word' }}>
              {this.state.error?.message || String(this.state.error)}
            </Alert>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Button variant="outlined" onClick={() => window.history.back()}>Go Back</Button>
              <Button variant="contained" startIcon={<Refresh />} onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </Box>
          </Paper>
        </Container>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
